/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import { Container, Row, Col, Alert } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { createSelector } from 'reselect';
import { Navigate } from 'react-router-dom';
import useQsParams from '../../../Components/Hooks/QueryString';
import { getKpisData } from '../../../slices/manager-panel/thunk';
import { formatCurrency } from '../../../helpers/format';
import PeriodFilter from './PeriodFilter';
import BreakageCard from './BreakageCard';
import KpiCard from './KpiCard';
import BreadCrumb from '../../../Components/Common/BreadCrumb';

const selectKpis = createSelector(
  (state) => state.ManagerPanel,
  ({ kpis }) => ({ kpis })
);

const RevenueDashboard = ({ t }) => {
  const dispatch = useDispatch();
  const { searchParams } = useQsParams();
  const [windowSizeDays, setWindowSizeDays] = useState(
    parseInt(searchParams.get('filters[windowSizeDays]'), 10) || 30
  );
  const authUser = JSON.parse(sessionStorage.getItem('authUser') ?? '{}');
  const isAdmin =
    !!authUser?.isAdmin ||
    authUser?.role?.name === 'Administrador' ||
    authUser?.role?.name === 'AdministradorOckCash' ||
    authUser?.roles?.[0]?.name === 'Administrador' ||
    authUser?.roles?.[0]?.name === 'AdministradorOckCash';

  useEffect(() => {
    document.title = `${t('revenue-dashboard.title')} | OCK`;
  }, [t]);

  const { kpis } = useSelector(selectKpis);

  useEffect(() => {
    const days = parseInt(searchParams.get('filters[windowSizeDays]'), 10) || 30;
    setWindowSizeDays(days);
  }, [searchParams]);

  useEffect(() => {
    dispatch(
      getKpisData({
        filters: { windowSizeDays },
      })
    );
  }, [dispatch, windowSizeDays]);

  const handlePeriodChange = (days) => {
    setWindowSizeDays(days);
    const url = new URL(window.location);
    url.searchParams.set('filters[windowSizeDays]', days);
    window.history.pushState({}, '', url);
  };

  const isLoading = kpis?.cards?.isLoading;
  const isError = kpis?.cards?.isError;

  const periodLabel = windowSizeDays === 30 ? '30 dias' : windowSizeDays === 60 ? '60 dias' : '90 dias';

  const repeatRate = ((kpis?.cards?.repeatPurchaseRate?.value || 0) * 100).toFixed(1);
  const frequency = kpis?.cards?.frequencyRate?.value || 0;
  const revenue = formatCurrency(kpis?.cards?.revenueIncrement?.value || 0);
  const avgTicket = formatCurrency(kpis?.cards?.averageTicket?.value || 0);
  const ltv = formatCurrency(kpis?.cards?.ltv?.value || 0);

  if (!isAdmin) {
    return <Navigate to="/resultados/vendas" />;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="breadcrumb-wrapper-no-border">
          <BreadCrumb
            title={t('revenue-dashboard.breadcrumbTitle')}
            pageTitle={t('ockcash')}
          />
        </div>

        {isError && (
          <Alert color="danger" className="mb-3">
            {t('revenue-dashboard.error') || 'Erro ao carregar os dados do dashboard. Tente novamente mais tarde.'}
          </Alert>
        )}

        <PeriodFilter selectedDays={windowSizeDays} onChange={handlePeriodChange} />

        <Row className="g-3 mb-3">
          <Col xs={12} md={6} xl={4}>
            {isLoading ? (
              <div className="skeleton card" style={{ height: '120px' }} />
            ) : (
              <KpiCard
                title={t('revenue-dashboard.cards.repeatPurchase.title')}
                value={`+${repeatRate}%`}
                icon="bx bx-repeat text-primary"
                tooltip={t('revenue-dashboard.cards.repeatPurchase.tooltip')}
              />
            )}
          </Col>
          <Col xs={12} md={6} xl={4}>
            {isLoading ? (
              <div className="skeleton card" style={{ height: '120px' }} />
            ) : (
              <KpiCard
                title={t('revenue-dashboard.cards.frequency.title')}
                value={`${frequency}x`}
                suffix={`/ ${periodLabel}`}
                icon="bx bx-time text-info"
                tooltip={t('revenue-dashboard.cards.frequency.tooltip')}
              />
            )}
          </Col>
          <Col xs={12} md={6} xl={4}>
            {isLoading ? (
              <div className="skeleton card" style={{ height: '120px' }} />
            ) : (
              <KpiCard
                title={t('revenue-dashboard.cards.revenueIncrement.title')}
                value={revenue}
                icon="bx bx-trending-up text-success"
                tooltip={t('revenue-dashboard.cards.revenueIncrement.tooltip')}
              />
            )}
          </Col>
        </Row>

        <Row className="g-3">
          <Col xs={12} md={6} xl={4}>
            {isLoading ? (
              <div className="skeleton card" style={{ height: '120px' }} />
            ) : (
              <KpiCard
                title={t('revenue-dashboard.cards.averageTicket.title')}
                value={avgTicket}
                icon="bx bx-receipt text-warning"
                tooltip={t('revenue-dashboard.cards.averageTicket.tooltip')}
              />
            )}
          </Col>
          <Col xs={12} md={6} xl={4}>
            {isLoading ? (
              <div className="skeleton card" style={{ height: '120px' }} />
            ) : (
              <KpiCard
                title={t('revenue-dashboard.cards.ltv.title')}
                value={ltv}
                icon="bx bx-pulse text-danger"
                tooltip={t('revenue-dashboard.cards.ltv.tooltip')}
              />
            )}
          </Col>
          <Col xs={12} xl={4}>
            <BreakageCard
              data={kpis?.cards?.breakage}
              isLoading={isLoading}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default withTranslation()(RevenueDashboard);
